/**
 * The guest "keep your progress" ask (#765 item 2, 2026-09-23).
 *
 * Nothing may interrupt a lesson in progress. The ask used to be a filled navy
 * button parked between the prompt card and the phase strip for the whole
 * session — the loudest object on the screen. Now, once the guest has finished
 * a round (the `eligible` signal, unchanged from the 2026-09-06 fix), ONE quiet
 * line shows under the card, once ever on this device, and after `lineMs` it
 * folds into a small outline icon in the top bar that stays available. Never a
 * modal, never a gate, never pauses playback — tapping either is the only way
 * into sign-in, and saving is typing an email.
 */
import { ref, computed, watch, getCurrentScope, onScopeDispose, type Ref } from 'vue'

export const GUEST_SAVE_LINE_SEEN_KEY = 'ssi-guest-save-line-seen'
export const GUEST_SAVE_LINE_MS = 8000

export function useGuestSaveNudge(
  eligible: Ref<boolean>,
  opts: { lineMs?: number; storage?: Pick<Storage, 'getItem' | 'setItem'> | null } = {},
) {
  const lineMs = opts.lineMs ?? GUEST_SAVE_LINE_MS
  const storage = (() => {
    if (opts.storage !== undefined) return opts.storage
    // Storage policies can throw on the localStorage getter itself.
    try { return typeof localStorage !== 'undefined' ? localStorage : null } catch { return null }
  })()
  const seen = (() => {
    try { return storage?.getItem(GUEST_SAVE_LINE_SEEN_KEY) === '1' } catch { return false }
  })()

  const lineShown = ref(false)
  const lineDone = ref(seen)
  let timer: ReturnType<typeof setTimeout> | null = null

  const fold = () => {
    if (timer) { clearTimeout(timer); timer = null }
    lineShown.value = false
    lineDone.value = true
  }

  watch(eligible, (ok) => {
    if (!ok) {
      // Signed in, or back to a fresh start: the line goes, and if it was
      // mid-show it counts as shown.
      if (lineShown.value) fold()
      return
    }
    if (lineDone.value || lineShown.value) return
    lineShown.value = true
    try { storage?.setItem(GUEST_SAVE_LINE_SEEN_KEY, '1') } catch { /* private mode */ }
    timer = setTimeout(fold, lineMs)
  }, { immediate: true })

  if (getCurrentScope()) onScopeDispose(() => { if (timer) clearTimeout(timer) })

  return {
    /** The one quiet line under the card. */
    showLine: lineShown,
    /** The small outline icon in the top bar, once the line has folded away. */
    showIcon: computed(() => eligible.value && lineDone.value && !lineShown.value),
    lineDone,
    fold,
  }
}
